import { useState, useCallback, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import { toastOptions } from "../common/toastOptions";
import axios from "axios";

interface CompressedFile {
  name: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  s3Key: string;
  downloadUrl: string;
}

function AudioCompress() {
  const [files, setFiles] = useState<File[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState<{
    [key: string]: number;
  }>({});
  const [compressionStatus, setCompressionStatus] = useState<{
    [key: string]: string;
  }>({});
  const [stepTimings, setStepTimings] = useState<{
    [key: string]: { upload: number; processing: number; compression: number };
  }>({});
  const [compressedFiles, setCompressedFiles] = useState<CompressedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const audioFiles = Array.from(e.dataTransfer.files).filter(
        (file) =>
          file.type === "audio/mpeg" ||
          file.type === "audio/wav" ||
          file.type === "audio/x-wav" ||
          file.type === "video/mp4" ||
          file.type === "audio/mp4" ||
          file.name.toLowerCase().endsWith(".wav") ||
          file.name.toLowerCase().endsWith(".mp3") ||
          file.name.toLowerCase().endsWith(".mp4")
      );

      if (audioFiles.length === 0) {
        toast.error("Please select only audio files (MP3, WAV, MP4)");
        return;
      }

      setFiles((prevFiles) => [...prevFiles, ...audioFiles]);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const audioFiles = Array.from(e.target.files).filter(
        (file) =>
          file.type === "audio/mpeg" ||
          file.type === "audio/wav" ||
          file.type === "audio/x-wav" ||
          file.type === "video/mp4" ||
          file.type === "audio/mp4" ||
          file.name.toLowerCase().endsWith(".wav") ||
          file.name.toLowerCase().endsWith(".mp3") ||
          file.name.toLowerCase().endsWith(".mp4")
      );

      if (audioFiles.length === 0) {
        toast.error("Please select only audio files (MP3, WAV, MP4)");
        return;
      }

      setFiles((prevFiles) => [...prevFiles, ...audioFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const compressAudioFile = async (file: File): Promise<CompressedFile> => {
    const startTime = Date.now();
    let uploadTime = 0;
    let processingTime = 0;
    let compressionTime = 0;

    try {
      // Step 1: Preparing for upload
      setCompressionProgress((prev) => ({ ...prev, [file.name]: 5 }));
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: "Preparing upload...",
      }));

      const presignedResponse = await axios.post("/api/get-upload-url", {
        userId: "audio-compression",
        fileName: file.name,
        fileType: file.type,
        bucketName: "police-narratives-ai-audio-compressed",
      });

      const { uploadURL: presignedUrl, key: s3Key } = presignedResponse.data;

      // Step 2: Uploading to S3
      const uploadStartTime = Date.now();
      setCompressionProgress((prev) => ({ ...prev, [file.name]: 20 }));
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: "Uploading to cloud...",
      }));

      await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const uploadPercent = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || file.size)
          );
          const overallProgress = 20 + Math.round(uploadPercent * 0.2); // Upload takes 20-40% of overall progress
          setCompressionProgress((prev) => ({
            ...prev,
            [file.name]: overallProgress,
          }));
          setCompressionStatus((prev) => ({
            ...prev,
            [file.name]: `Uploading... ${uploadPercent}%`,
          }));
        },
      });

      uploadTime = Date.now() - uploadStartTime;

      // Step 3: Processing file
      const processingStartTime = Date.now();
      setCompressionProgress((prev) => ({ ...prev, [file.name]: 40 }));
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: "Processing audio...",
      }));

      const lambdaResponse = await axios.post("/api/compress-audio", {
        bucketName: "police-narratives-ai-audio-compressed",
        fileName: s3Key,
      });

      processingTime = Date.now() - processingStartTime;

      // Step 4: Processing audio
      const compressionStartTime = Date.now();
      setCompressionProgress((prev) => ({ ...prev, [file.name]: 70 }));

      // All MP4 files get audio extracted
      const isMP4 = file.name.toLowerCase().endsWith(".mp4");

      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: isMP4 ? "Extracting audio..." : "Compressing audio...",
      }));

      console.log("lambdaResponse.data");
      console.log(lambdaResponse.data);

      const { outputKey, originalSize, convertedSize } = lambdaResponse.data;

      // Validate that we received the outputKey
      if (!outputKey) {
        throw new Error("Lambda function did not return outputKey");
      }

      compressionTime = Date.now() - compressionStartTime;

      // Store timing data
      setStepTimings((prev) => ({
        ...prev,
        [file.name]: {
          upload: uploadTime,
          processing: processingTime,
          compression: compressionTime,
        },
      }));

      // Generate download URL for the compressed file
      setCompressionProgress((prev) => ({ ...prev, [file.name]: 90 }));
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: "Finalizing...",
      }));

      const awsRegion =
        process.env.NODE_ENV === "production" ? "us-gov-west-1" : "us-west-2";
      const downloadUrl = `https://police-narratives-ai-audio-compressed.s3.${awsRegion}.amazonaws.com/${outputKey}`;

      const compressionRatio =
        ((originalSize - convertedSize) / originalSize) * 100;

      // Determine the compressed filename based on the original file and output
      let compressedFileName: string;

      if (isMP4) {
        // All MP4 files get audio extracted to MP3
        compressedFileName = file.name.replace(/\.[^/.]+$/, "") + "_audio.mp3";
      } else {
        // Audio files (WAV, MP3) get compressed to MP3
        compressedFileName =
          file.name.replace(/\.[^/.]+$/, "") + "_compressed.mp3";
      }

      // Delete the original uncompressed file
      setCompressionStatus((prev) => ({
        ...prev,
        [file.name]: "Cleaning up...",
      }));

      try {
        await axios.delete("/api/delete-s3-file", {
          data: {
            bucketName: "police-narratives-ai-audio-compressed",
            fileName: s3Key,
          },
        });
      } catch (deleteError) {
        console.warn("Failed to delete original file:", deleteError);
        // Don't throw error for cleanup failure
      }

      setCompressionProgress((prev) => ({ ...prev, [file.name]: 100 }));
      setCompressionStatus((prev) => ({ ...prev, [file.name]: "Complete!" }));

      return {
        name: compressedFileName,
        originalSize: originalSize,
        compressedSize: convertedSize,
        compressionRatio: Math.max(0, compressionRatio),
        s3Key: outputKey,
        downloadUrl: downloadUrl,
      };
    } catch (error) {
      console.error("Compression error:", error);
      throw new Error("Failed to compress audio file");
    }
  };

  const compressFiles = async () => {
    if (files.length === 0) {
      toast.error("Please select audio files to compress");
      return;
    }

    setCompressing(true);
    setCompressedFiles([]);

    try {
      const compressionPromises = files.map(async (file, index) => {
        setCompressionProgress((prev) => ({ ...prev, [file.name]: 0 }));

        try {
          const compressedFile = await compressAudioFile(file);
          setCompressionProgress((prev) => ({ ...prev, [file.name]: 100 }));

          // Add the compressed file immediately when it's done
          setCompressedFiles((prev) => [...prev, compressedFile]);

          return compressedFile;
        } catch (error) {
          throw error;
        }
      });

      await Promise.all(compressionPromises);

      toast.success("All files compressed successfully!");
    } catch (error) {
      console.error("Compression error:", error);
      toast.error("Error compressing files. Please try again.");
    } finally {
      setCompressing(false);
    }
  };

  const downloadFile = async (compressedFile: CompressedFile) => {
    try {
      // Get a presigned download URL from the backend
      const response = await axios.post("/api/get-compressed-download-url", {
        bucketName: "police-narratives-ai-audio-compressed",
        fileName: compressedFile.s3Key,
        originalFileName: compressedFile.name,
      });

      const { downloadUrl } = response.data;

      // Create download link using the presigned URL
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = compressedFile.name;
      a.style.display = "none";
      a.target = "_blank"; // Open in new tab for security

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file. Please try again.");
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatTime = (milliseconds: number): string => {
    if (milliseconds < 1000) {
      return `${milliseconds}ms`;
    }
    const seconds = (milliseconds / 1000).toFixed(1);
    return `${seconds}s`;
  };

  return (
    <>
      <div className="min-h-screen bg-base-200">
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-base-content flex items-center gap-1">
                Audio Compressor
              </h1>
              <p className="text-gray-600 dark:text-base-content/70 mt-1">
                Compress MP3, WAV, and MP4 files to reduce their size. MP4 files
                will have audio extracted to MP3.
              </p>
            </div>
          </div>

          {/* File upload area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              compressing
                ? "border-gray-300 dark:border-base-300 bg-gray-100 dark:bg-base-300"
                : "border-blue-300 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:border-blue-400"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              multiple
              accept=".mp3,.wav,.mp4"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileSelect}
              disabled={compressing}
            />

            <div className="flex flex-col items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-blue-500 dark:text-blue-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                />
              </svg>
              <p className="text-lg font-medium mb-2">
                Drag and drop audio files here, or{" "}
                <button
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-300 font-semibold"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={compressing}
                  type="button"
                >
                  browse
                </button>
              </p>
              <p className="text-base-content/60 text-sm">
                Supports MP3, WAV, and MP4 files. MP4 files will be converted to
                MP3 audio.
              </p>
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-3">
                Selected Files ({files.length})
              </h2>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between bg-base-100 p-3 rounded-lg border border-base-300 shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-base-content/40"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-base-content/80">
                          {file.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {compressionProgress[file.name] > 0 &&
                        compressionProgress[file.name] < 100 && (
                          <div className="w-72">
                            <div className="h-2 bg-base-300 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{
                                  width: `${compressionProgress[file.name]}%`,
                                }}
                              ></div>
                            </div>
                            <div className="flex justify-between mt-1">
                              <p className="text-xs text-base-content/60">
                                {compressionStatus[file.name] ||
                                  "Processing..."}
                              </p>
                              <p className="text-xs text-base-content/60">
                                {compressionProgress[file.name]}%
                              </p>
                            </div>
                          </div>
                        )}

                      {compressionProgress[file.name] === 100 && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}

                      {!compressing &&
                        compressionProgress[file.name] !== 100 && (
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700"
                            type="button"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Compress button */}
          <div className="mt-6">
            <button
              onClick={compressFiles}
              disabled={files.length === 0 || compressing}
              className={`px-6 py-3 rounded-lg font-medium text-white ${
                files.length === 0 || compressing
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              type="button"
            >
              {compressing ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Compressing...
                </div>
              ) : (
                "Compress Files"
              )}
            </button>
          </div>

          {/* Compressed files section */}
          {compressedFiles.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">
                  Compressed Files ({compressedFiles.length})
                </h2>
              </div>
              <div className="space-y-3">
                {compressedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-base-100 p-4 rounded-lg border border-base-300 shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-base-content/80">
                          {file.name}
                        </p>
                        <div className="flex space-x-4 text-xs text-base-content/60">
                          <span>
                            Original: {formatFileSize(file.originalSize)}
                          </span>
                          <span>
                            {(() => {
                              // Check if this was audio extraction (large MP4 -> MP3)
                              const wasAudioExtraction =
                                file.name.includes("_audio.mp3");
                              return wasAudioExtraction
                                ? "Extracted: "
                                : "Compressed: ";
                            })()}
                            {formatFileSize(file.compressedSize)}
                          </span>
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {file.compressionRatio.toFixed(1)}% reduction
                          </span>
                        </div>
                        {(() => {
                          // Find the timing data by looking for the original filename
                          const originalFileName = file.name.replace(
                            "_compressed.mp3",
                            ""
                          );
                          const timingKey = Object.keys(stepTimings).find(
                            (key) => key.startsWith(originalFileName)
                          );
                          const timing = timingKey
                            ? stepTimings[timingKey]
                            : null;

                          return (
                            timing && (
                              <div className="flex space-x-3 text-xs text-base-content/40 mt-1">
                                <span>Upload: {formatTime(timing.upload)}</span>
                                <span>
                                  Processing: {formatTime(timing.processing)}
                                </span>
                                <span>
                                  Compression: {formatTime(timing.compression)}
                                </span>
                              </div>
                            )
                          );
                        })()}
                      </div>
                    </div>

                    <button
                      onClick={() => downloadFile(file)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      type="button"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Toaster position="bottom-center" toastOptions={toastOptions} />
    </>
  );
}

export default AudioCompress;
